import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';

export type LayerAnalyticsPieSlice = { label: string; count: number };

type LayerAnalyticsPieChartProps = {
  title: string;
  slices: LayerAnalyticsPieSlice[];
};

/** ECharts draws on a canvas, so CSS variables have to be resolved first. */
const readCssColor = (name: string): string | undefined => {
  if (typeof document === 'undefined') return undefined;
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
  return value || undefined;
};

/**
 * A titled donut chart with percentage labels and a legend underneath, for the
 * figures shown on the analysis cards. Shows a short message instead when every
 * slice is zero.
 */
export const LayerAnalyticsPieChart: React.FC<LayerAnalyticsPieChartProps> = ({
  title,
  slices,
}) => {
  const total = slices.reduce((sum, { count }) => sum + count, 0);

  const option = useMemo<EChartsOption>(() => {
    const textColor = readCssColor('--text-white');
    return {
      tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
      legend: {
        orient: 'horizontal',
        bottom: 0,
        textStyle: { color: textColor },
      },
      series: [
        {
          name: title,
          type: 'pie',
          radius: ['40%', '62%'],
          center: ['50%', '40%'],
          label: {
            show: true,
            color: textColor,
            // Zero-count slices are not drawn, so they get no "0%" label.
            formatter: ({ value, percent }) => (value ? `${percent}%` : ''),
          },
          labelLine: { show: true },
          // Zero-count slices are not drawn but stay in the legend.
          data: slices.map(({ label, count }) => ({
            name: label,
            value: count,
          })),
        },
      ],
    };
  }, [slices, title]);

  return (
    <div className="d-flex flex-column gap-2 rounded border p-3">
      <h6 className="fs-6 fw-bold mb-0">{title}</h6>
      {total === 0 ? (
        <em className="text-muted">No data</em>
      ) : (
        <ReactECharts
          option={option}
          notMerge
          style={{ height: 260, width: '100%' }}
        />
      )}
    </div>
  );
};
